import { useTranslation } from 'react-i18next';
import { createQColumnHelper, QBox, QDataTable, TableMenuItem, useCurrentUser } from '@qualio/ui-components';
import { EventStepStatus, UserTask } from '../../../types';
import { canCompleteTaskFn } from '../../../context';
import { calcTaskDueDate } from '../../../utils/datetimeUtils';
import { useEventPermissions } from '../../../hooks';
import { DateTime } from 'luxon';

const mapTaskStatusToLabel = (status: string, t: (key: string) => string) => {
  switch (status) {
    case 'open':
      return t('common.labels.open');
    case 'closed_success':
      return t('common.labels.completed');
    case 'closed_fail':
      return t('common.labels.canceled');
    default:
      return status;
  }
};

const isTaskOpen = (task: UserTask) => task.status === 'open';
const isTaskClosed = (task: UserTask) => task.status === 'closed_success' || task.status === 'closed_fail';

type StepTasksListProps = {
  onCompleteTask: (task: UserTask) => void;
  onReopenTask: (task: UserTask) => void;
  onOpenTask: (task: UserTask) => void;
  isEventOwner: boolean;
  stepStatus: string;
};

const StepTasksList = ({ onCompleteTask, onReopenTask, onOpenTask, stepStatus }: StepTasksListProps) => {
  const { t } = useTranslation();
  const { userId, tz } = useCurrentUser();
  const { canCompleteAnyTask, canReopenTask, isEventCollaboratorsEnabled } = useEventPermissions();

  const columnHelper = createQColumnHelper<UserTask>();

  const statusColumn = columnHelper.status((task: UserTask) => mapTaskStatusToLabel(task.status, t), {
    id: 'status',
    header: t('common.labels.status'),
    statuses: { Open: 'blue', Completed: 'green', Canceled: 'red' },
    maxWidth: '15%',
  });
  const ownerColumn = columnHelper.text('owner', {
    id: 'owner',
    header: t('common.labels.assignedTo'),
    minWidth: '25%',
  });
  const dueColumn = columnHelper.date(
    (task: UserTask) => {
      const dueDate = calcTaskDueDate(task.created_at, task.time_limit, tz);

      const dueDateLuxon = DateTime.fromJSDate(dueDate);

      // To prevent a task due today from being marked as "past due" before the day is over,
      // check if the due date is today. If it is, set the deadline tp end of the day.
      if (dueDateLuxon.hasSame(DateTime.local(), 'day')) {
        return dueDateLuxon.endOf('day').toJSDate();
      }

      return dueDate;
    },
    {
      id: 'due_at',
      header: t('common.labels.due'),
      maxWidth: '15%',
      highlightPastDates: (task: UserTask) => isTaskOpen(task),
    },
  );

  const titleColumn = columnHelper.textAction('title', onOpenTask, {
    id: 'title',
    header: t('common.labels.taskName'),
  });

  const columns = [titleColumn, ownerColumn, dueColumn, statusColumn];
  if (stepStatus === EventStepStatus.enum.open) {
    columns.push(
      columnHelper.menu({
        items: (
          <>
            <TableMenuItem
              onClick={(task: UserTask) => {
                onCompleteTask(task);
              }}
              isDisabled={(task: UserTask) =>
                isTaskClosed(task) || !canCompleteTaskFn(userId, task, canCompleteAnyTask, isEventCollaboratorsEnabled)
              }
              isHidden={(task: UserTask) => isTaskClosed(task)}
            >
              {t('tasks.actions.completeTask')}
            </TableMenuItem>
            <TableMenuItem
              onClick={onReopenTask}
              isDisabled={() => !canReopenTask}
              isHidden={(task: UserTask) => isTaskOpen(task)}
            >
              {t('common.labels.reopen')}
            </TableMenuItem>
          </>
        ),
      }),
    );
  }

  return (
    <QBox p={2}>
      <QDataTable columns={columns} />
    </QBox>
  );
};

export default StepTasksList;
